/**
 * CheckoutRedirect — bridges the old `/services/:serviceId/checkout` route
 * into the new cart flow.
 *
 * Session 19 removed in-app payment. Anything that still links to a service's
 * checkout (bookmarks, a clinic's payment-link SMS) should now ADD that
 * service to the cart and land on the cart page, where the user reviews the
 * proforma and continues to the landing payment page.
 *
 * We resolve the service from the catalog (same cache key as the list/detail
 * pages) so the cart line shows the real name + price, then redirect to /cart.
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Stack, CircularProgress } from '@mui/material';
import Container from '@/components/PageSheet';

import { servicesApi } from '@/services/api';
import { addItem } from '@/store/cartSlice';

export function CheckoutRedirect() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const done = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: () => servicesApi.listServices({ pageSize: 50 }),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (done.current || isLoading) return;
    done.current = true;
    const service = (data?.data?.services ?? []).find((s) => s.id === serviceId);
    if (service) {
      dispatch(
        addItem({ id: service.id, name: service.name, priceToman: service.priceToman }),
      );
    }
    navigate('/cart', { replace: true });
  }, [data, isLoading, serviceId, dispatch, navigate]);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    </Container>
  );
}

export default CheckoutRedirect;
